import React, { useRef } from "react";
import { toPng } from "html-to-image";
import { Award, Download, FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LitraMark } from "@/components/Brand";
import { exportNodePdf } from "@/lib/pdf";
import { toast } from "sonner";

export const CertificateCard = ({ certificate, username }) => {
  const ref = useRef(null);
  const key = certificate.code || certificate.id;

  const download = async () => {
    if (!ref.current) return;
    try {
      const url = await toPng(ref.current, { pixelRatio: 2, cacheBust: true });
      const a = document.createElement("a");
      a.download = `litrametry-certificate-${key}.png`;
      a.href = url;
      a.click();
      toast.success("Certificate downloaded");
    } catch (e) {
      toast.error("Could not render certificate");
    }
  };

  const downloadPdf = async () => {
    if (!ref.current) return;
    try {
      const url = await toPng(ref.current, { pixelRatio: 2, cacheBust: true });
      const img = new Image();
      img.src = url;
      await new Promise((res, rej) => { img.onload = res; img.onerror = rej; });
      await exportNodePdf(url, img.width, img.height, `litrametry-certificate-${key}.pdf`);
      toast.success("Certificate PDF downloaded");
    } catch (e) {
      toast.error("Could not render PDF");
    }
  };

  const issued = certificate.issued_at ? new Date(certificate.issued_at).toLocaleDateString() : "";

  return (
    <div data-testid={`certificate-card-${key}`}>
      <div
        ref={ref}
        className="rounded-2xl p-6 relative overflow-hidden gold-edge"
        style={{ background: "radial-gradient(620px 320px at 50% -10%, rgba(176,141,60,0.12), transparent 60%), #0B0C0E" }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2"><LitraMark size={20} /><span className="font-display font-semibold text-sm">Litrametry</span></div>
          <Award className="w-5 h-5 gold-text" />
        </div>
        <div className="mono-chip gold-text mt-6">CERTIFICATE OF ACHIEVEMENT</div>
        <div className="text-xl font-display font-semibold mt-1">{certificate.title}</div>
        <p className="text-sm text-[var(--bone-muted)] mt-2 leading-relaxed">{certificate.description}</p>
        {username && (
          <div className="mt-5 text-sm text-[var(--bone-muted)]">Awarded to <span className="font-display text-[var(--bone)]">{username}</span></div>
        )}
        <div className="mt-1 text-xs text-[var(--bone-faint)] mono-chip">{issued}</div>
      </div>
      <div className="mt-2 flex gap-2">
        <Button variant="outline" size="sm" className="glass" onClick={download} data-testid={`certificate-download-${key}`}>
          <Download className="w-4 h-4 mr-2" /> PNG
        </Button>
        <Button variant="outline" size="sm" className="glass gold-edge gold-text" onClick={downloadPdf} data-testid={`certificate-pdf-${key}`}>
          <FileDown className="w-4 h-4 mr-2" /> PDF
        </Button>
      </div>
    </div>
  );
};

export default CertificateCard;
